import { useState } from "react";
import { Link } from "react-router-dom";
import { useBoqs, type BoqRow } from "../../api/boq";
import { formatMoney } from "../../api/money";
import { StatusChip, type Tone } from "../../components/ui";
import { count } from "./JobsRail";
import { jobLink } from "./pipeline";

/** A draft that is no longer the job's: withdrawn or replaced. */
const DEAD = new Set(["rejected", "superseded"]);

/** Where a draft stands, as its row says it. */
export function draftStanding(row: BoqRow): { label: string; tone: Tone } {
  if (row.copied) {
    return { label: "Copied", tone: "ok" };
  }
  return row.job_id === null ? { label: "Waiting for the quote", tone: "draft" } : { label: "On a job", tone: "info" };
}

/** Where a draft opens: step 1 of its job once a supplier has quoted it, and
 * the bill on its own before that. */
export function draftLink(row: BoqRow): string {
  return row.job_id === null ? `/boq/${row.id}` : jobLink(row.job_id, "draft");
}

/** The office's draft BOQs, at the top of the rail.
 *
 * A draft is started before any supplier has quoted, so until one does there
 * is no job to list it under; without this it could only be found from the
 * bill screen. Unsearched, the rail shows the drafts still being worked on --
 * a copied draft is finished with, and its job carries it. A search shows
 * every draft that matches its work order, site or name, copied or not. */
export default function DraftRows({ search }: { search: string }) {
  const [open, setOpen] = useState(true);
  const boqs = useBoqs(search);
  const drafts = (boqs.data?.rows ?? []).filter(
    (row) => row.is_draft && !DEAD.has(row.status) && (search !== "" || !row.copied),
  );
  if (boqs.isError) {
    return <p className="notice bad cm-rail-error">{boqs.error.message}</p>;
  }
  if (drafts.length === 0) {
    return null;
  }
  return (
    <section className="cm-queue" aria-labelledby="queue-drafts-head">
      <button
        type="button"
        id="queue-drafts-head"
        className="cm-queue-head"
        aria-expanded={open}
        aria-controls="queue-drafts"
        onClick={() => setOpen((now) => !now)}
        data-testid="queue-drafts"
      >
        <span className="cm-chev" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
        <span className="cm-queue-label">Draft BOQs</span>
        <span className="cm-queue-count mine">{count(drafts.length)}</span>
        <span className="cm-queue-hint">priced before the quote</span>
      </button>
      {open ? (
        <div id="queue-drafts">
          {drafts.map((row) => {
            const standing = draftStanding(row);
            return (
              <Link key={row.id} to={draftLink(row)} className="cm-row" data-testid={`draft-row-${row.id}`}>
                <span className="cm-row-top">
                  <span className="cm-row-ref mono">{row.work_order_no ?? row.name}</span>
                  <StatusChip tone={standing.tone}>{standing.label}</StatusChip>
                  <span className="cm-row-money">{formatMoney(row.total_excl_vat)}</span>
                </span>
                <span className="cm-row-bottom">
                  <span className="cm-row-where">
                    {row.site?.name ?? "site not linked"} · Draft BOQ, {row.line_count} line
                    {row.line_count === 1 ? "" : "s"}
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
